import { QdsNativeField, type FieldValidity } from '../shared/native-field.js';
import { isAttrTruthy, reflectBoolean } from '../shared/form-control.js';

export type QdsPeriodValue = string | [string, string];
export type QdsPeriodGranularity = 'month' | 'quarter' | 'year';
interface PeriodDraft { year: string; unit: string; }
interface PeriodValidity extends FieldValidity { badInput: boolean; rangeUnderflow: boolean; rangeOverflow: boolean; }

/**
 * Calendar reporting periods with native year and localized unit editors.
 * @element qds-period-picker
 * @attr {'month'|'quarter'|'year'} granularity - Calendar unit; default month.
 * @attr {boolean} range - Start/end pair rather than a scalar period.
 * @attr {string|[string,string]} value - Canonical default/reset string or JSON pair.
 * @attr {string} min - Inclusive canonical minimum in the current granularity.
 * @attr {string} max - Inclusive canonical maximum in the current granularity.
 * @attr {string} locale - Month/summary locale; falls back to document/browser locale.
 * @attr {string} start-label - Range start label; default Start.
 * @attr {string} end-label - Range end label; default End.
 * @attr {string} name - Scalar or repeated native submission key.
 * @attr {string} form - External native form ID.
 * @attr {string} label - Visible group name.
 * @attr {string} aria-label - Explicit accessible group/control name.
 * @attr {string} aria-labelledby - External naming text IDs.
 * @attr {string} hint - Helper text.
 * @attr {string} error-text - Validation message override.
 * @attr {boolean} required - Requires every active endpoint.
 * @attr {boolean} readonly - Focusable/submitted without user edits.
 * @attr {boolean} disabled - Not interactive or submitted.
 * @attr {boolean} invalid - Application-owned invalid state.
 * @attr {'input'|'blur'|'submit'} validate-on - Feedback timing; default blur.
 * @attr {'sm'|'md'|'lg'} size - Field size; editors retain 44px targets.
 * @property {string|[string,string]} value - Current YYYY-MM, YYYY-Qn or YYYY, or cloned pair; empty is ''.
 * @property {string|[string,string]} defaultValue - Attribute-backed reset value.
 * @property {string} [readonly] formattedValue - Localized visible summary; never changes canonical values.
 * @csspart container - Native fieldset containing the period editors.
 * @csspart label - Group legend.
 * @csspart endpoint - Stable start/end editor groups.
 * @csspart input - Native year editors and unit selects.
 * @csspart value - Localized summary.
 * @csspart hint - Helper text.
 * @csspart error - Constraint feedback.
 * @fires {{component:HTMLElement;value:string|[string,string];granularity:'month'|'quarter'|'year';range:boolean}} input - Native user edit; owner writes are silent.
 * @fires {{component:HTMLElement;value:string|[string,string];granularity:'month'|'quarter'|'year';range:boolean}} change - Native committed edit.
 * @fires {{component:HTMLElement;valid:boolean;validity:{valid:boolean;valueMissing:boolean;tooShort:boolean;tooLong:boolean;customError:boolean;badInput:boolean;rangeUnderflow:boolean;rangeOverflow:boolean};message:string}} qds-validity-change - Validity/message transition.
 */
export class QdsPeriodPicker extends QdsNativeField {
  static override get observedAttributes(): string[] {
    return [...super.observedAttributes, 'value', 'granularity', 'range', 'min', 'max', 'locale', 'start-label', 'end-label', 'form'];
  }
  private _value: QdsPeriodValue = '';
  private _drafts: PeriodDraft[] = [{ year: '', unit: '' }, { year: '', unit: '' }];
  private _editors: (HTMLInputElement | HTMLSelectElement)[] = [];
  private _forceValue = false;
  private _optionsKey = '';
  protected override get _upgradeNames(): string[] { return ['granularity', 'range', 'min', 'max', 'locale', 'startLabel', 'endLabel', 'defaultValue', 'value']; }
  get granularity(): QdsPeriodGranularity { const value = this.getAttribute('granularity'); return value === 'quarter' || value === 'year' ? value : 'month'; }
  set granularity(value: QdsPeriodGranularity) { this.setAttribute('granularity', value); }
  get range(): boolean { return isAttrTruthy(this.getAttribute('range')); }
  set range(value: boolean) { reflectBoolean(this, 'range', value); }
  get min(): string { return this._canonical(this.getAttribute('min')); }
  set min(value: string) { this.setAttribute('min', value ?? ''); }
  get max(): string { return this._canonical(this.getAttribute('max')); }
  set max(value: string) { this.setAttribute('max', value ?? ''); }
  get locale(): string { return this.getAttribute('locale') || this.closest('[lang]')?.getAttribute('lang') || this.ownerDocument.documentElement.lang || ''; }
  set locale(value: string) { this.setAttribute('locale', value ?? ''); }
  get startLabel(): string { return this.getAttribute('start-label') || 'Start'; }
  set startLabel(value: string) { this.setAttribute('start-label', value ?? ''); }
  get endLabel(): string { return this.getAttribute('end-label') || 'End'; }
  set endLabel(value: string) { this.setAttribute('end-label', value ?? ''); }
  get value(): QdsPeriodValue { return Array.isArray(this._value) ? [...this._value] : this._value; }
  set value(value: QdsPeriodValue) { this._dirty = true; this._assign(value); this._sync(); }
  get defaultValue(): QdsPeriodValue {
    const text = this.getAttribute('value') || '';
    if (text.startsWith('[')) { try { return this._normalize(JSON.parse(text)); } catch { return this._normalize(''); } }
    return this._normalize(text);
  }
  set defaultValue(value: QdsPeriodValue) { this.setAttribute('value', Array.isArray(value) ? JSON.stringify(value) : String(value ?? '')); }
  override get validity(): PeriodValidity { return { badInput: false, rangeUnderflow: false, rangeOverflow: false, ...super.validity }; }
  get formattedValue(): string { return this._values().map(value => this._format(value)).filter(Boolean).join(' – '); }
  override attributeChangedCallback(name?: string): void {
    if (!this._dirty) this._assign(this.defaultValue);
    else if (name === 'granularity' || name === 'range') this._assign(this._value);
    super.attributeChangedCallback();
  }
  private _canonical(value: unknown): string {
    if (typeof value !== 'string') return '';
    const pattern = this.granularity === 'year' ? /^\d{4}$/ : this.granularity === 'quarter' ? /^\d{4}-Q[1-4]$/ : /^\d{4}-(0[1-9]|1[0-2])$/;
    return pattern.test(value) && Number(value.slice(0, 4)) > 0 ? value : '';
  }
  private _normalize(value: unknown): QdsPeriodValue {
    if (this.range) return Array.isArray(value) ? [this._canonical(value[0]), this._canonical(value[1])] : [this._canonical(value), ''];
    return this._canonical(Array.isArray(value) ? value[0] : value);
  }
  private _values(): string[] { return Array.isArray(this._value) ? this._value : [this._value]; }
  private _assign(value: unknown): void {
    this._value = this._normalize(value);
    this._drafts = [0, 1].map(index => {
      const period = this._values()[index] || '';
      return { year: period.slice(0, 4), unit: this.granularity === 'quarter' ? period.slice(6) : period.slice(5) };
    });
    this._forceValue = true;
  }
  private _compose(draft: PeriodDraft): string {
    const year = Number(draft.year);
    if (!draft.year || !Number.isInteger(year) || year < 1 || year > 9999) return '';
    const prefix = String(year).padStart(4, '0');
    return this._canonical(this.granularity === 'year' ? prefix : `${prefix}-${this.granularity === 'quarter' ? 'Q' : ''}${draft.unit}`);
  }
  private _rank(value: string): number { return Number(value.slice(0, 4)) * 12 + (this.granularity === 'quarter' ? Number(value.slice(6)) * 3 : Number(value.slice(5)) || 0); }
  private _format(value: string, monthOnly = false): string {
    if (!value) return '';
    try {
      if (this.granularity === 'month') return new Intl.DateTimeFormat(this.locale || undefined, { ...(monthOnly ? {} : { year: 'numeric' as const }), month: 'long', timeZone: 'UTC' }).format(new Date(`${value}-01T00:00:00Z`));
      const year = new Intl.NumberFormat(this.locale || undefined, { useGrouping: false, minimumIntegerDigits: 4 }).format(Number(value.slice(0, 4)));
      return this.granularity === 'quarter' ? `${value.slice(5)} ${year}` : year;
    } catch { return monthOnly ? value.slice(5) : value; }
  }
  protected _render(): void {
    if (!this._dirty) this._assign(this.defaultValue);
    this.shadowRoot!.innerHTML = `<style>${this._styles}
      :host { display: inline-block; width: 100%; }
      fieldset { border: 0; margin: 0; padding: 0; min-inline-size: 0; }
      legend { padding: 0; }
      .period-editors { display: grid; gap: 1rem; }
      .endpoint { display: flex; flex-wrap: wrap; gap: .5rem; align-items: end; }
      .endpoint-name { flex-basis: 100%; font-size: .875rem; font-weight: 600; }
      .editor-label { display: grid; gap: .25rem; flex: 1 1 8rem; font-size: .875rem; }
      .qds-control-surface { width: 100%; min-width: 0; height: max(var(--qds-control-height), 44px); min-height: 44px; padding: .625rem .75rem; }
      .period-value { margin: .25rem 0; color: var(--theme-text-secondary); font-size: .875rem; overflow-wrap: anywhere; }
      .qds-error { color: var(--theme-text-primary); border-inline-start: 3px solid var(--theme-error); padding-inline-start: .5rem; }
    </style><fieldset part="container"><legend class="qds-label" part="label"><span class="label-text"></span><span class="qds-required-marker" aria-hidden="true">*</span></legend><div class="period-editors">${[0, 1].map(index => `<div class="endpoint" part="endpoint" data-endpoint="${index}"><span class="endpoint-name"></span><label class="editor-label" for="${this._id}-${index}-year">Year<input type="number" id="${this._id}-${index}-year" class="qds-control-surface" part="input" data-endpoint="${index}" data-field="year" step="1" inputmode="numeric"></label><label class="editor-label unit-label" for="${this._id}-${index}-unit"><span class="unit-name"></span><select id="${this._id}-${index}-unit" class="qds-control-surface" part="input" data-endpoint="${index}" data-field="unit"></select></label></div>`).join('')}</div><p class="period-value" part="value" id="${this._id}-value"></p><div class="qds-feedback"><div class="qds-hint" part="hint" id="${this._id}-hint"></div><div class="qds-error" part="error" id="${this._id}-error" aria-live="polite"></div></div></fieldset>`;
    this._editors = [...this.shadowRoot!.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input,select')];
    this._control = this._editors[0] as HTMLInputElement;
  }
  protected _syncControl(): void {
    const key = `${this.granularity}:${this.locale}`;
    for (const [index, draft] of this._drafts.entries()) {
      const group = this.shadowRoot!.querySelector<HTMLElement>(`.endpoint[data-endpoint="${index}"]`)!;
      group.hidden = index === 1 && !this.range;
      const caption = group.querySelector<HTMLElement>('.endpoint-name')!;
      caption.hidden = !this.range; caption.textContent = index ? this.endLabel : this.startLabel;
      const year = this._editors[index * 2] as HTMLInputElement;
      year.min = this.min.slice(0, 4) || '1'; year.max = this.max.slice(0, 4) || '9999';
      if (this._forceValue || year.value !== draft.year) year.value = draft.year;
      const select = this._editors[index * 2 + 1] as HTMLSelectElement;
      group.querySelector<HTMLElement>('.unit-label')!.hidden = this.granularity === 'year';
      group.querySelector<HTMLElement>('.unit-name')!.textContent = this.granularity === 'quarter' ? 'Quarter' : 'Month';
      if (key !== this._optionsKey) {
        const values = Array.from({ length: this.granularity === 'year' ? 0 : this.granularity === 'quarter' ? 4 : 12 }, (_, unit) => this.granularity === 'quarter' ? String(unit + 1) : String(unit + 1).padStart(2, '0'));
        const options = ['', ...values].map(value => {
          const option = this.ownerDocument.createElement('option'); option.value = value;
          option.textContent = value ? this.granularity === 'quarter' ? `Q${value}` : this._format(`2000-${value}`, true) : 'Choose';
          return option;
        });
        select.replaceChildren(...options);
      }
      if (this._forceValue || select.value !== draft.unit) select.value = draft.unit;
    }
    this._optionsKey = key; this._forceValue = false;
    const output = this.shadowRoot!.querySelector<HTMLElement>('[part=value]')!;
    output.textContent = this.formattedValue; output.hidden = !output.textContent;
  }
  protected override _sync(): void {
    super._sync(); if (!this._control) return;
    const name = this._control.getAttribute('aria-label') || this.label || 'Period';
    this.shadowRoot!.querySelector('fieldset')!.setAttribute('aria-label', name);
    for (const [index, editor] of this._editors.entries()) {
      const endpoint = Math.floor(index / 2), unit = index % 2 === 1;
      editor.disabled = this.disabled || (endpoint === 1 && !this.range) || (unit && this.granularity === 'year');
      editor.required = this.required;
      if (!unit) (editor as HTMLInputElement).readOnly = this.readonly;
      editor.setAttribute('aria-label', `${name}${this.range ? ` — ${endpoint ? this.endLabel : this.startLabel}` : ''} — ${unit ? this.granularity === 'quarter' ? 'Quarter' : 'Month' : 'Year'}`);
      editor.setAttribute('aria-readonly', String(this.readonly)); editor.setAttribute('aria-required', String(this.required));
      if (index) for (const attr of ['aria-describedby', 'aria-invalid']) { const value = this._control.getAttribute(attr); if (value) editor.setAttribute(attr, value); else editor.removeAttribute(attr); }
    }
  }
  protected override _counterDescription(): string { return this.formattedValue ? `${this._id}-value` : ''; }
  protected _formValue(): string | FormData {
    if (!this.range) return this._value as string;
    const data = new this.ownerDocument.defaultView!.FormData();
    if (this.name) for (const value of this._values()) data.append(this.name, value);
    return data;
  }
  protected _formState(): string { return JSON.stringify(this._value); }
  protected _reset(): void { this._assign(this.defaultValue); }
  protected _restore(state: string): void { try { this._assign(JSON.parse(state)); } catch { /* Preserve the current value for malformed state. */ } }
  protected _constraints(): { flags: Partial<PeriodValidity>; message: string } {
    const values = this._values(), drafts = this._drafts.slice(0, this.range ? 2 : 1);
    const incomplete = drafts.some((draft, index) => (draft.year || draft.unit) && !values[index]) || (this.range && Boolean(values[0]) !== Boolean(values[1]));
    const badInput = incomplete || this._editors.some((editor, index) => index % 2 === 0 && (this.range || index === 0) && editor.validity.badInput) || (this.range && Boolean(values[0] && values[1]) && this._rank(values[1]) < this._rank(values[0]));
    const valueMissing = this.required && values.some(value => !value);
    const rangeUnderflow = Boolean(this.min) && values.some(value => value && this._rank(value) < this._rank(this.min));
    const rangeOverflow = Boolean(this.max) && values.some(value => value && this._rank(value) > this._rank(this.max));
    const message = badInput ? 'Complete every period and keep the end at or after the start.' : valueMissing ? 'Choose a complete reporting period.' : rangeUnderflow ? `Choose a period at or after ${this.min}.` : rangeOverflow ? `Choose a period at or before ${this.max}.` : '';
    return { flags: { badInput, valueMissing, rangeUnderflow, rangeOverflow }, message };
  }
  private _onEdit = (event: Event): void => {
    const editor = event.target as HTMLInputElement | HTMLSelectElement;
    if (!this._editors.includes(editor)) return;
    event.stopPropagation();
    if (this.disabled || this.readonly) { this._forceValue = true; this._sync(); return; }
    const endpoint = Number(editor.dataset.endpoint), field = editor.dataset.field as keyof PeriodDraft;
    this._drafts[endpoint][field] = editor.value;
    const values = this._drafts.map(draft => this._compose(draft));
    this._value = this.range ? [values[0], values[1]] : values[0];
    this._dirty = true; if (this.validateOn === 'input') this._showError = true;
    this._sync(); this._emit(event.type as 'input' | 'change', { value: this.value, granularity: this.granularity, range: this.range });
  };
  private _guard = (event: Event): void => {
    const editor = event.target as HTMLSelectElement;
    if (!this.readonly || editor.tagName !== 'SELECT') return;
    if (event.type === 'keydown') {
      const key = event as KeyboardEvent;
      if (key.ctrlKey || key.metaKey || (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', ' ', 'Enter'].includes(key.key) && key.key.length !== 1)) return;
    }
    event.preventDefault(); editor.focus();
  };
  protected _bind(): void {
    this.shadowRoot!.addEventListener('input', this._onEdit); this.shadowRoot!.addEventListener('change', this._onEdit);
    this.shadowRoot!.addEventListener('blur', this._onBlur, true);
    for (const name of ['pointerdown', 'click', 'keydown']) this.shadowRoot!.addEventListener(name, this._guard);
  }
  protected _unbind(): void {
    this.shadowRoot!.removeEventListener('input', this._onEdit); this.shadowRoot!.removeEventListener('change', this._onEdit);
    this.shadowRoot!.removeEventListener('blur', this._onBlur, true);
    for (const name of ['pointerdown', 'click', 'keydown']) this.shadowRoot!.removeEventListener(name, this._guard);
  }
}
if (!customElements.get('qds-period-picker')) customElements.define('qds-period-picker', QdsPeriodPicker);
